import { ApplicationError } from '../domain/errors';

export const ITEM_IMAGE_POLICY = {
  version: 1,
  mimeType: 'image/jpeg',
  maxSide: 1200,
  maxBytes: 200 * 1024,
  // Aimed at the 200 KiB ceiling: most 1200 px photos fit on the first encode.
  initialQuality: 0.65,
  upscale: false,
  background: '#ffffff',
} as const;

export const IMAGE_PROCESSING = {
  acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'],
  outputType: ITEM_IMAGE_POLICY.mimeType,
  initialQuality: ITEM_IMAGE_POLICY.initialQuality,
  maxDimension: ITEM_IMAGE_POLICY.maxSide,
  maxInputBytes: 20 * 1024 * 1024,
  maxOutputBytes: ITEM_IMAGE_POLICY.maxBytes,
} as const;

/** Coordinates refer to the decoded, orientation-corrected source. */
export type SquareCrop = { x: number; y: number; size: number };
export type PreparedItemImage = {
  file: File;
  width: number;
  height: number;
  bytes: number;
  policyVersion: 1;
};
export interface ProcessImageOptions {
  signal?: AbortSignal;
  onProgress?: (progress: number) => void;
}

const preparedFiles = new WeakSet<File>();

/** Register a File this app just encoded to the item-image policy, so later
 * `ensureItemImage` calls return it without another decode/encode pass.
 */
export const markItemImagePrepared = (file: File): File => {
  preparedFiles.add(file);
  return file;
};

const invalid = (message: string): ApplicationError =>
  new ApplicationError('validation', message);
const checkAbort = (signal?: AbortSignal): void => {
  if (signal?.aborted)
    throw new DOMException('Image processing cancelled.', 'AbortError');
};

const checkInput = (source: Blob): void => {
  if (
    !IMAGE_PROCESSING.acceptedTypes.includes(
      source.type as (typeof IMAGE_PROCESSING.acceptedTypes)[number],
    )
  ) {
    throw invalid('Choose a JPEG, PNG, or WebP image.');
  }
  if (!source.size || source.size > IMAGE_PROCESSING.maxInputBytes) {
    throw invalid('The selected image is empty or too large.');
  }
};

type DecodedImage = {
  image: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
};

const decodeImage = async (source: Blob): Promise<DecodedImage> => {
  checkInput(source);
  if (typeof createImageBitmap === 'function') {
    try {
      // The default applies EXIF orientation. Do not use `none`, which would
      // disagree with the browser image/crop preview for rotated phone photos.
      const image = await createImageBitmap(source);
      return {
        image,
        width: image.width,
        height: image.height,
        close: () => image.close(),
      };
    } catch {
      // Some browsers support a format in <img> but not createImageBitmap.
    }
  }
  const url = URL.createObjectURL(source);
  const image = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(invalid('This image could not be decoded.'));
      image.src = url;
    });
    return {
      image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      close: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
};

export const centeredSquareCrop = (
  width: number,
  height: number,
): SquareCrop => {
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width < 1 ||
    height < 1
  ) {
    throw invalid('The image dimensions are invalid.');
  }
  const size = Math.min(width, height);
  return { x: (width - size) / 2, y: (height - size) / 2, size };
};

export const inspectImage = async (
  source: Blob,
): Promise<{ width: number; height: number }> => {
  const decoded = await decodeImage(source);
  try {
    centeredSquareCrop(decoded.width, decoded.height);
    return { width: decoded.width, height: decoded.height };
  } finally {
    decoded.close();
  }
};

const readHeader = (blob: Blob): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(invalid('The image could not be read.'));
    reader.readAsArrayBuffer(blob.slice(0, 3));
  });

/** Validate bytes and decoded dimensions, never caller-supplied metadata. */
export const validateItemImage = async (
  file: Blob,
): Promise<{ width: number; height: number; bytes: number }> => {
  if (
    file.type !== ITEM_IMAGE_POLICY.mimeType ||
    !file.size ||
    file.size > ITEM_IMAGE_POLICY.maxBytes
  ) {
    throw invalid('Item photos must be JPEG images no larger than 200 KiB.');
  }
  const header = await readHeader(file);
  if (header[0] !== 0xff || header[1] !== 0xd8 || header[2] !== 0xff) {
    throw invalid('The image is not a valid JPEG.');
  }
  const { width, height } = await inspectImage(file);
  if (width !== height || width > ITEM_IMAGE_POLICY.maxSide) {
    throw invalid(
      'Item photos must be square and no larger than 1200 × 1200 pixels.',
    );
  }
  return { width, height, bytes: file.size };
};

const encode = (canvas: HTMLCanvasElement, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(invalid('The image could not be encoded.'));
      },
      ITEM_IMAGE_POLICY.mimeType,
      quality,
    );
  });

export const prepareItemImage = async (
  source: Blob,
  options: {
    crop: SquareCrop;
    signal?: AbortSignal;
    onProgress?: (percent: number) => void;
  },
): Promise<PreparedItemImage> => {
  checkAbort(options.signal);
  const decoded = await decodeImage(source);
  try {
    checkAbort(options.signal);
    const { x, y, size } = options.crop;
    if (
      ![x, y, size].every(Number.isFinite) ||
      x < 0 ||
      y < 0 ||
      size < 1 ||
      x + size > decoded.width ||
      y + size > decoded.height
    ) {
      throw invalid('Select a square crop inside the image.');
    }
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context)
      throw invalid('Image processing is unavailable in this browser.');
    options.onProgress?.(10);
    let side = Math.min(Math.floor(size), ITEM_IMAGE_POLICY.maxSide);
    // Each attempt samples the original, avoiding successive JPEG degradation.
    for (let resize = 0; resize < 12; resize += 1) {
      canvas.width = side;
      canvas.height = side;
      context.fillStyle = ITEM_IMAGE_POLICY.background;
      context.fillRect(0, 0, side, side);
      context.drawImage(decoded.image, x, y, size, size, 0, 0, side, side);
      for (const quality of [
        ITEM_IMAGE_POLICY.initialQuality,
        0.55,
        0.45,
        0.35,
      ]) {
        checkAbort(options.signal);
        const blob = await encode(canvas, quality);
        checkAbort(options.signal);
        if (blob.size <= ITEM_IMAGE_POLICY.maxBytes) {
          const name =
            source instanceof File
              ? source.name.replace(/\.[^.]+$/, '')
              : 'item-photo';
          const file = new File([blob], `${name || 'item-photo'}.jpg`, {
            type: ITEM_IMAGE_POLICY.mimeType,
          });
          const metadata = await validateItemImage(file);
          checkAbort(options.signal);
          if (metadata.width !== side || metadata.height !== side)
            throw invalid('The encoded image dimensions are invalid.');
          options.onProgress?.(100);
          preparedFiles.add(file);
          return {
            file,
            ...metadata,
            policyVersion: ITEM_IMAGE_POLICY.version,
          };
        }
      }
      options.onProgress?.(Math.min(90, 20 + resize * 6));
      if (side === 1) break;
      side = Math.max(1, Math.floor(side * 0.8));
    }
    throw invalid('The processed image exceeds the upload limit.');
  } finally {
    decoded.close();
  }
};

/** Compatibility entry point: all item-image callers receive the same policy. */
export const ensureItemImage = async (
  source: Blob,
  options: ProcessImageOptions = {},
): Promise<File> => {
  checkAbort(options.signal);
  if (source instanceof File && preparedFiles.has(source)) {
    options.onProgress?.(100);
    return source;
  }
  const { width, height } = await inspectImage(source);
  const prepared = await prepareItemImage(source, {
    ...options,
    crop: centeredSquareCrop(width, height),
  });
  return prepared.file;
};

export const processImage = (
  file: File,
  options: ProcessImageOptions = {},
): Promise<File> => ensureItemImage(file, options);
